import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Trophy, Medal, Crown, TrendingUp, TrendingDown, Minus,
    ChevronUp, ChevronDown
} from 'lucide-react'


interface Student {
    rank: number
    name: string
    avatar: string
    score: number
    testsCompleted: number
    accuracy: number
    trend: 'up' | 'down' | 'stable'
    change: number
    isCurrentUser?: boolean
}

const leaderboardData: Student[] = [
    { rank: 1, name: 'Priya Sharma', avatar: 'PS', score: 685, testsCompleted: 12, accuracy: 95.2, trend: 'stable', change: 0 },
    { rank: 2, name: 'Rahul Verma', avatar: 'RV', score: 672, testsCompleted: 12, accuracy: 93.5, trend: 'up', change: 2 },
    { rank: 3, name: 'Ananya Gupta', avatar: 'AG', score: 658, testsCompleted: 11, accuracy: 91.8, trend: 'up', change: 1 },
    { rank: 4, name: 'Arun Student', avatar: 'AS', score: 645, testsCompleted: 12, accuracy: 90.2, trend: 'up', change: 3, isCurrentUser: true },
    { rank: 5, name: 'Vikram Singh', avatar: 'VS', score: 638, testsCompleted: 12, accuracy: 88.9, trend: 'down', change: 1 },
    { rank: 6, name: 'Neha Patel', avatar: 'NP', score: 625, testsCompleted: 11, accuracy: 87.5, trend: 'stable', change: 0 },
    { rank: 7, name: 'Karan Mehta', avatar: 'KM', score: 612, testsCompleted: 10, accuracy: 86.2, trend: 'up', change: 2 },
    { rank: 8, name: 'Simran Kaur', avatar: 'SK', score: 598, testsCompleted: 12, accuracy: 85.0, trend: 'down', change: 3 },
    { rank: 9, name: 'Arjun Reddy', avatar: 'AR', score: 585, testsCompleted: 11, accuracy: 84.3, trend: 'up', change: 1 },
    { rank: 10, name: 'Divya Nair', avatar: 'DN', score: 572, testsCompleted: 10, accuracy: 83.8, trend: 'stable', change: 0 },
]

const subjectLeaderboards = {
    physics: [
        { rank: 1, name: 'Rahul Verma', avatar: 'RV', score: 172, accuracy: 95.5 },
        { rank: 2, name: 'Priya Sharma', avatar: 'PS', score: 168, accuracy: 93.3 },
        { rank: 3, name: 'Arun Student', avatar: 'AS', score: 155, accuracy: 86.1, isCurrentUser: true },
    ],
    chemistry: [
        { rank: 1, name: 'Ananya Gupta', avatar: 'AG', score: 175, accuracy: 97.2 },
        { rank: 2, name: 'Arun Student', avatar: 'AS', score: 162, accuracy: 90.0, isCurrentUser: true },
        { rank: 3, name: 'Vikram Singh', avatar: 'VS', score: 158, accuracy: 87.8 },
    ],
    biology: [
        { rank: 1, name: 'Priya Sharma', avatar: 'PS', score: 348, accuracy: 96.7 },
        { rank: 2, name: 'Neha Patel', avatar: 'NP', score: 335, accuracy: 93.1 },
        { rank: 3, name: 'Arun Student', avatar: 'AS', score: 328, accuracy: 91.1, isCurrentUser: true },
    ],
}

export function LeaderboardView() {
    const [activeTab, setActiveTab] = useState<'overall' | 'physics' | 'chemistry' | 'biology'>('overall')

    const getRankStyle = (rank: number) => {
        if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500'
        if (rank === 2) return 'bg-gradient-to-r from-slate-300 to-slate-400'
        if (rank === 3) return 'bg-gradient-to-r from-amber-500 to-orange-600'
        return 'bg-slate-200 dark:bg-slate-700'
    }

    const currentUserRank = leaderboardData.find(s => s.isCurrentUser)

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-accent-gold" />
                    Leaderboard
                </h2>
                <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg overflow-x-auto">
                    {(['overall', 'physics', 'chemistry', 'biology'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium rounded-md capitalize transition-all whitespace-nowrap ${activeTab === tab
                                ? 'bg-white dark:bg-slate-700 shadow text-primary-blue'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Your Rank Card */}
            {currentUserRank && (
                <GlassmorphicCard className="p-4 md:p-6 bg-gradient-to-r from-primary-blue/10 to-accent-gold/10 border-l-4 border-l-primary-blue">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center text-white font-bold text-xl">
                                {currentUserRank.avatar}
                            </div>
                            <div>
                                <p className="font-bold text-theme-primary text-lg">Your Rank</p>
                                <p className="text-theme-secondary text-sm">Keep pushing! You're doing great.</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-4xl font-bold text-primary-blue">#{currentUserRank.rank}</p>
                            <div className="flex items-center justify-end gap-1 text-sm">
                                {currentUserRank.trend === 'up' && (
                                    <span className="text-green-500 flex items-center gap-0.5">
                                        <TrendingUp className="h-4 w-4" />
                                        +{currentUserRank.change}
                                    </span>
                                )}
                                {currentUserRank.trend === 'down' && (
                                    <span className="text-red-500 flex items-center gap-0.5">
                                        <TrendingDown className="h-4 w-4" />
                                        -{currentUserRank.change}
                                    </span>
                                )}
                                {currentUserRank.trend === 'stable' && (
                                    <span className="text-slate-400 flex items-center gap-0.5">
                                        <Minus className="h-4 w-4" />
                                        No change
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </GlassmorphicCard>
            )}

            {/* Top 3 Podium */}
            {activeTab === 'overall' && (
                <div className="grid grid-cols-3 gap-2 md:gap-4">
                    {/* 2nd Place */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="order-1"
                    >
                        <GlassmorphicCard className="p-3 md:p-4 text-center h-full flex flex-col justify-end">
                            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center text-white font-bold text-lg md:text-xl mx-auto mb-2 border-4 border-white shadow-lg">
                                {leaderboardData[1].avatar}
                            </div>
                            <Medal className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                            <p className="font-semibold text-theme-primary text-xs md:text-sm truncate">{leaderboardData[1].name}</p>
                            <p className="text-lg md:text-xl font-bold text-primary-blue">{leaderboardData[1].score}</p>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* 1st Place */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0 }}
                        className="order-0 md:order-0 transform md:-translate-y-4"
                    >
                        <GlassmorphicCard className="p-3 md:p-4 text-center bg-gradient-to-b from-yellow-50 to-white dark:from-yellow-900/20 dark:to-slate-900 border-2 border-yellow-200 dark:border-yellow-800/30">
                            <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white font-bold text-xl md:text-2xl mx-auto mb-2 border-4 border-white shadow-xl">
                                {leaderboardData[0].avatar}
                            </div>
                            <Crown className="h-7 w-7 text-yellow-500 mx-auto mb-1" />
                            <p className="font-bold text-theme-primary text-sm md:text-base truncate">{leaderboardData[0].name}</p>
                            <p className="text-xl md:text-2xl font-bold text-primary-blue">{leaderboardData[0].score}</p>
                        </GlassmorphicCard>
                    </motion.div>

                    {/* 3rd Place */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="order-2"
                    >
                        <GlassmorphicCard className="p-3 md:p-4 text-center h-full flex flex-col justify-end">
                            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg md:text-xl mx-auto mb-2 border-4 border-white shadow-lg">
                                {leaderboardData[2].avatar}
                            </div>
                            <Medal className="h-6 w-6 text-amber-600 mx-auto mb-1" />
                            <p className="font-semibold text-theme-primary text-xs md:text-sm truncate">{leaderboardData[2].name}</p>
                            <p className="text-lg md:text-xl font-bold text-primary-blue">{leaderboardData[2].score}</p>
                        </GlassmorphicCard>
                    </motion.div>
                </div>
            )}

            {/* Full Leaderboard */}
            <GlassmorphicCard className="overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <h3 className="font-bold text-theme-primary">
                        {activeTab === 'overall' ? 'Full Ranking' : `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Rankings`}
                    </h3>
                    <span className="text-xs text-theme-secondary">
                        Updated 2 hours ago
                    </span>
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {(activeTab === 'overall' ? leaderboardData : subjectLeaderboards[activeTab as keyof typeof subjectLeaderboards]).map((student, i) => (
                        <motion.div
                            key={student.rank}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={`flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${student.isCurrentUser ? 'bg-primary-blue/5' : ''
                                }`}
                        >
                            <div className="flex items-center gap-3 md:gap-4">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${student.rank <= 3 ? getRankStyle(student.rank) + ' text-white' : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary'
                                    }`}>
                                    {student.rank}
                                </div>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${student.isCurrentUser
                                    ? 'bg-gradient-to-br from-primary-blue to-accent-gold text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-theme-primary'
                                    }`}>
                                    {student.avatar}
                                </div>
                                <div>
                                    <p className={`font-medium text-sm ${student.isCurrentUser ? 'text-primary-blue' : 'text-theme-primary'}`}>
                                        {student.name} {student.isCurrentUser && <span className="text-xs">(You)</span>}
                                    </p>
                                    <p className="text-xs text-theme-secondary">{student.accuracy}% accuracy</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                {'trend' in student && (
                                    <div className="hidden sm:flex items-center gap-1 text-xs">
                                        {student.trend === 'up' && <ChevronUp className="h-4 w-4 text-green-500" />}
                                        {student.trend === 'down' && <ChevronDown className="h-4 w-4 text-red-500" />}
                                        {student.trend === 'stable' && <Minus className="h-4 w-4 text-slate-400" />}
                                    </div>
                                )}
                                <div className="text-right">
                                    <p className="font-bold text-primary-blue text-lg">{student.score}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </GlassmorphicCard>
        </div>
    )
}
